import type { AuthoredLevel, GameState, TileState, TileType } from "../domain/types";
import { difficultyFor, tutorialLevels } from "../content/Content";
import { SeededRandom } from "../infrastructure/rng/SeededRandom";
import { validateSolvability } from "./LevelValidator";

export const tile = (type: TileType = "empty"): TileState => ({
  type,
  revealed: false,
  visited: false,
  walkable: type !== "collapse",
  consumed: false,
  dangerLevel: ["trap", "poison"].includes(type) ? 1 : 0,
  discoveryState: "unknown",
  omen: ["curse", "poison"].includes(type) ? "cursed_chamber"
    : ["relic", "relicFragment", "gem"].includes(type) ? "possible_relic"
      : ["trap", "portal", "multiplier"].includes(type) ? "ancient_energy" : "none",
});

const symbolTypes: Record<string, TileType> = {
  ".": "empty", c: "coin", t: "treasure", g: "gem", r: "relic", "^": "trap",
  x: "poison", O: "portal", f: "freeze", m: "multiplier", "#": "collapse", E: "exit",
  q: "curse", R: "relicFragment", s: "secretClue", M: "mystery",
};

function baseState(
  levelId: string, seed: number, size: number, grid: TileState[], start: number,
  exit: number, moves: number, goal: number, timeLimitSeconds = 120,
  objective = { relicFragmentsRequired: 0, secretRoomsRequired: 0, requireExit: true },
  mission = { targetMoves: Math.ceil(moves * .25), targetScore: goal * 80, avoidTraps: true },
): GameState {
  return {
    levelId, seed, status: "playing", size, grid, playerIndex: start, exitIndex: exit,
    hp: 100, movesRemaining: moves, score: 0, treasuresCollected: 0,
    treasureGoal: goal, objective, mission, trapsTriggered: 0, combo: { current: 1, best: 1, window: 0 },
    multiplierMoves: 0, timer: { remainingSeconds: timeLimitSeconds, initialSeconds: timeLimitSeconds },
    statusEffect: "none", fragileTrapMultiplier: 1,
    rewards: { gold: 0, gems: 0, relics: 0, relicFragments: 0, secretClues: 0 },
  };
}

export function createAuthoredLevel(definition: AuthoredLevel): GameState {
  if (definition.rows.length !== definition.size ||
      definition.rows.some(row => row.length !== definition.size)) {
    throw new Error(`Invalid authored level dimensions: ${definition.id}`);
  }
  let start = -1;
  let exit = -1;
  const grid = definition.rows.flatMap((row, y) => [...row].map((symbol, x) => {
    const index = y * definition.size + x;
    if (symbol === "P") { start = index; return tile(); }
    if (symbol === "E") exit = index;
    const visibleType = { C: "coin", T: "treasure", G: "gem" }[symbol] as TileType | undefined;
    const type = visibleType ?? symbolTypes[symbol];
    if (!type) throw new Error(`Unknown tile symbol "${symbol}" in ${definition.id}`);
    const result = tile(type);
    if (visibleType) {
      result.revealed = true;
      result.discoveryState = "revealed";
    }
    return result;
  }));
  if (start < 0 || exit < 0) throw new Error(`Authored level ${definition.id} needs P and E`);
  grid[start].revealed = true;
  grid[start].visited = true;
  grid[start].discoveryState = "revealed";
  const state = baseState(
    definition.id, definition.levelNumber, definition.size, grid, start, exit,
    definition.moves, definition.treasureGoal, 120,
    {
      relicFragmentsRequired: definition.relicFragmentsRequired ?? 0,
      secretRoomsRequired: definition.secretRoomsRequired ?? 0,
      requireExit: definition.requireExit ?? true,
    },
    definition.mission ?? {
      targetMoves: Math.max(2, Math.floor(definition.moves * .3)),
      targetScore: Math.max(100, definition.treasureGoal * 100),
      avoidTraps: definition.levelNumber % 2 === 1,
    },
  );
  if (!validateSolvability(state).solvable) throw new Error(`Authored level ${definition.id} is not solvable`);
  return state;
}

export function createLevel(level: number, seed: number): GameState {
  const tutorial = tutorialLevels.find(item => item.levelNumber === level);
  if (tutorial) return createAuthoredLevel(tutorial);

  const config = difficultyFor(level);
  const size = config.gridSize;
  const count = size * size;
  const start = count - size;
  const exit = size - 1;
  const rng = new SeededRandom(seed);
  const grid = Array.from({ length: count }, () => tile());
  grid[start].revealed = true;
  grid[start].visited = true;
  grid[start].discoveryState = "revealed";
  grid[exit] = tile("exit");

  // Reserve a deterministic L-shaped route, then place enough treasure on it.
  const safeRoute: number[] = [];
  for (let row = size - 1; row >= 0; row--) safeRoute.push(row * size);
  for (let column = 1; column < size; column++) safeRoute.push(column);
  const routeTreasureSlots = safeRoute.slice(1, -1);
  rng.shuffle(routeTreasureSlots).slice(0, config.treasureGoal)
    .forEach((index, position) => { grid[index] = tile(position % 3 === 2 ? "treasure" : "coin"); });

  const pool: TileType[] = [];
  for (const [type, amount] of Object.entries(config.tileCounts)) {
    for (let index = 0; index < (amount ?? 0); index++) pool.push(type as TileType);
  }
  const reserved = new Set([start, exit, ...routeTreasureSlots.filter(index => grid[index].type !== "empty")]);
  const slots = rng.shuffle(Array.from({ length: count }, (_, index) => index))
    .filter(index => !reserved.has(index) && !safeRoute.includes(index));
  pool.forEach((type, index) => { if (slots[index] !== undefined) grid[slots[index]] = tile(type); });

  // Apply Candy Crush style pacing curve modifiers:
  // Pos 6: Hard Spike 🔥 (fewer moves)
  // Pos 7: Break Level 🌟 (extra moves & rewards)
  // Pos 10: Super Hard Boss 💀 (higher treasure goal)
  const posInChapter = ((level - 1) % 10) + 1;
  let movesMult = config.movesMultiplier;
  let goal = config.treasureGoal;

  if (posInChapter === 6) {
    movesMult *= 0.85; // Hard spike: tighter move count
  } else if (posInChapter === 7) {
    movesMult *= 1.25; // Break level: generous move count
  } else if (posInChapter === 10) {
    goal = Math.min(size, goal + 1); // Boss level: extra treasure requirement
    movesMult *= 0.90;
  }

  const state = baseState(
    `campaign-${level}`, seed, size, grid, start, exit,
    Math.max(5, Math.floor(count * movesMult)), goal, config.timeLimitSeconds ?? 120,
  );
  const report = validateSolvability(state);
  if (!report.solvable) throw new Error(`Generator produced unsolvable level ${level}:${seed}`);
  return state;
}

export function tutorialDefinition(level: number): AuthoredLevel | undefined {
  return tutorialLevels.find(item => item.levelNumber === level);
}
